import { ArrowRight, ArrowUpRight, Check, ExternalLink, MessageCircle, TrendingUp } from "lucide-react";
import { MF_SERVICES, MF_PROCESS, MF_LINKS, COLORS, COMPANY } from "@/lib/site";
import { Reveal } from "@/components/Reveal";

export default function MutualFunds() {
  return (
    <div data-testid="mutual-funds-page">
      <section className="bg-[#FDFCF0] border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Reveal>
              <div className="inline-flex items-center gap-2 text-sm font-medium mb-5 px-3 py-1 rounded-full" style={{ backgroundColor: "#EAF0FF", color: COLORS.blue }}>
                <TrendingUp size={16} /> Our original business
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-[#1A1A1A]">Mutual fund distribution</h1>
              <p className="mt-3 text-lg font-medium" style={{ color: COLORS.green }}>Mutual Funds sahi hai — and it's really easy to invest in them.</p>
              <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
                At Aumonline, our mission is to give clients comprehensive, competent, personalised
                and effective solutions for meeting their investment goals. We provide simple,
                unbiased and uncluttered investment advice that adds value to your quality of life
                and leads to clear, actionable steps.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="bg-white rounded-md border border-gray-200 p-8">
              <div className="text-sm text-slate-500 uppercase tracking-wide">Assets under management</div>
              <div className="text-5xl font-bold mt-1" style={{ color: COLORS.blue }}>{COMPANY.aum}</div>
              <div className="mt-6 border-t border-gray-200 pt-5 text-sm text-slate-600 space-y-1">
                <div>AMFI-registered Mutual Fund Distributor</div>
                <div className="font-medium text-[#1A1A1A]">{COMPANY.arn} · EUIN {COMPANY.euin}</div>
                <div>NISM Series V-A certified (valid till {COMPANY.nismValidTill})</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white border-b border-gray-200" data-testid="mf-saathi-card">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14">
          <Reveal>
            <div className="rounded-md border border-gray-200 bg-[#FDFCF0] p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-8">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 text-sm font-medium mb-4 px-3 py-1 rounded-full bg-white border border-gray-200" style={{ color: COLORS.green }}>
                  <MessageCircle size={16} /> New — chat with MF Saathi
                </div>
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1A1A1A]">Not sure where to begin? Start with a conversation.</h2>
                <p className="mt-3 text-slate-600 leading-relaxed max-w-2xl">
                  MF Saathi is Aumonline's mutual fund education assistant. It explains mutual fund
                  concepts in plain, simple words — what a SIP is, how risk works, what expense
                  ratios mean — so you can invest with understanding. It does not recommend
                  specific schemes and it is not investment advice.
                </p>
                <p className="mt-3 text-sm text-slate-500 leading-relaxed max-w-2xl">
                  The chat will ask for your name, and offer to email you a copy of the conversation.
                  If you share your email, the transcript is sent to you and shared with Aumonline
                  for follow-up. Sharing your email is entirely optional.
                </p>
              </div>
              <div className="shrink-0">
                <a href="https://mf.aumonline.in/?src=aumonline" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-md px-6 py-3 text-white font-medium transition-colors duration-150 hover:opacity-90" style={{ backgroundColor: COLORS.green }} data-testid="mf-saathi-chat-button">
                  Chat with MF Saathi <ExternalLink size={16} />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white border-b border-gray-200" data-testid="mf-services">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <Reveal>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1A1A]">What we help you with</h2>
          </Reveal>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            {MF_SERVICES.map((s, i) => {
              const external = s.link.startsWith("http");
              return (
                <Reveal key={s.title} delay={i * 0.06}>
                  <a
                    href={s.link}
                    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    className="block rounded-md border border-gray-200 p-6 h-full transition-colors duration-150 hover:border-gray-400 hover:bg-[#FDFCF0]"
                    data-testid={`mf-service-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <h3 className="text-xl font-semibold text-[#1A1A1A] flex items-center gap-2">
                      {s.title}
                      <ArrowUpRight size={18} style={{ color: COLORS.green }} />
                    </h3>
                    <p className="mt-2 text-slate-600 leading-relaxed">{s.desc}</p>
                  </a>
                </Reveal>
              );
            })}
          </div>
          <Reveal>
            <div className="mt-8 rounded-md border border-gray-200 p-6 bg-[#FDFCF0]">
              <h3 className="text-lg font-semibold text-[#1A1A1A]">More services we offer</h3>
              <div className="mt-3 flex flex-wrap gap-2 text-sm">
                {["Investment Strategy", "Client Desk (Wealth Management)", "Map Investments", "NJ E-Wealth A/c Service", "Product Basket"].map((x) => (
                  <span key={x} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-gray-200 px-3 py-1 text-slate-700">
                    <Check size={14} style={{ color: COLORS.green }} /> {x}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#FDFCF0] border-b border-gray-200" data-testid="mf-process">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <Reveal>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1A1A]">How it works</h2>
          </Reveal>
          <div className="mt-10 grid md:grid-cols-4 gap-6">
            {MF_PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.06}>
                <div className="bg-white rounded-md border border-gray-200 p-6 h-full">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: COLORS.blue }}>{p.step}</div>
                  <h3 className="mt-4 text-lg font-semibold text-[#1A1A1A]">{p.title}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed text-sm">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white" data-testid="mf-cta">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] tracking-tight">Ready to start investing?</h2>
            <p className="mt-2 text-slate-600">Open a free investment account, or talk to us first — whichever you prefer.</p>
          </div>
          <div className="flex flex-wrap gap-4">
            <a href={MF_LINKS.openAccount} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-md px-6 py-3 text-white font-medium transition-colors duration-150 hover:opacity-90" style={{ backgroundColor: COLORS.blue }} data-testid="mf-open-account">
              Open a free account <ExternalLink size={16} />
            </a>
            <a href="/contact" className="inline-flex items-center gap-2 rounded-md px-6 py-3 font-medium border transition-colors duration-150 hover:bg-gray-50" style={{ borderColor: COLORS.green, color: COLORS.green }} data-testid="mf-contact">
              Talk to us <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      <section className="bg-[#FDFCF0] border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <p className="text-xs text-slate-500 leading-relaxed">
            Mutual fund investments are subject to market risks; read all scheme related documents
            carefully. Aumonline is an AMFI-registered Mutual Fund Distributor ({COMPANY.arn}, EUIN {COMPANY.euin}).
            Distribution services are provided on a non-advisory basis. Past performance is not indicative of future returns.
          </p>
        </div>
      </section>
    </div>
  );
}
